/* eslint-disable @hhgtech/hhg-customs/img-require-loading */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { LOCALE } from '@hhgtech/hhg-components/types'
import { Box, Stack } from '@mantine/core'
import Cookies from 'js-cookie'
import Head from 'next/head'
import { v5 as uuidv5 } from 'uuid'

import { callApiNew } from 'api/axios'
import { formatSpotlight } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { Loading } from 'components/atoms/loading'
import { NotFound } from 'components/organisms/notFound'
import { Props as DynamicSpotlightPageProps } from 'components/pages/dynamicSpotlight'
import { handleFetchMoreData } from 'components/pages/spotlight/fetch'
import CarePathSectionTemplate from 'components/templates/carePath'
import {
  addPageCookies,
  getPageCookies,
  setAudienceTermId,
} from 'components/templates/carePath/helpers'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS } from 'interfaces/constants'
import { SERVICE_CONNECTION_LATEST_ACCESS_COOKIES } from 'interfaces/constants/cookies'
import type { HealthTool, Spotlight } from 'interfaces/types'

import { CarePathDefaultTemplateHandler } from './detectPage'

export type Props = {
  isMobile: boolean
  healthTools: HealthTool[]
  pageType: 'detail' | null
  noReFetch?: boolean
  hideSticky?: boolean
  isInline?: boolean
} & DynamicSpotlightPageProps

const LIST_PAGE_NON_USE_COOKIE = ['imunitas-dan-inteligensi']

const SpotlightCarePathTemplate = ({
  spotlight: spotlightInit,
  pageType: pageTypeInit,
  noReFetch,
  hideSticky,
  footerInfo,
  dataNavMenuContent,
  isInline,
}: Props) => {
  const {
    query: { slug },
    push,
    pathname,
    asPath,
    locale,
  } = useRouterExtend()
  const pageName = useMemo(() => slug?.[0] || '', [slug])
  const isDisableCookiePage = LIST_PAGE_NON_USE_COOKIE.includes(pageName)
  const [pageType, setPageType] = useState<'detail' | 'selection' | null>(
    pageTypeInit,
  )
  const [spotlight, setSpotlight] = useState<Spotlight>(spotlightInit)
  const curPage = slug?.[0] || ''
  const audienceSlug = slug?.[1] || ''
  const funnelSlug = slug?.[2] || ''

  const fetchSpotlight = useCallback(
    async (pageSlug: string, groupId: string, additionalSlug = '') => {
      if (noReFetch) return
      try {
        setPageType(null)
        const params: Record<string, string> = { slug: pageSlug, groupId }
        if (additionalSlug) {
          params['additionalSlug'] = additionalSlug
        }
        const res = await callApiNew(PATHS.SPOTLIGHT.GET_SPOTLIGHT_LANDING, {
          params,
        })
        if (res.data) {
          const newData = await handleFetchMoreData(formatSpotlight(res.data))
          setSpotlight(newData)
        }
        setPageType('detail')
      } catch (err) {
        console.log(err)
        setPageType('detail')
      }
    },
    [noReFetch],
  )

  const handleRedirectPage = useCallback(
    ({
      pageSlug,
      audienceSlug,
      funnelSlug,
      shallow = true,
    }: {
      pageSlug: string
      audienceSlug?: string
      funnelSlug?: string
      shallow?: boolean
    }) => {
      const hasPrefix = pathname === '/spotlight/[...slug]'
      const slugs = [pageSlug, audienceSlug, funnelSlug]
        .filter(Boolean)
        .join('/')
      hasPrefix
        ? push('/spotlight/[...slug]', `/spotlight/${slugs}`, { shallow })
        : push('[...slug]', `/${slugs}`, {
            shallow,
          })
    },
    [],
  )

  useEffect(() => {
    const cookieInfo = getPageCookies(curPage)
    if (!asPath.includes('#')) {
      if (!audienceSlug) {
        if (spotlight.listGroup.length > 1) {
          if (!isDisableCookiePage && cookieInfo) {
            const curAudienceDataByTermId = spotlight.listGroup?.filter(
              (i) => i.termId === cookieInfo.audienceTermId,
            )[0]
            const curAudienceDataByGroupId = spotlight.listGroup?.filter(
              (i) => i.groupId === cookieInfo.audienceId,
            )[0]
            const curAudienceData = [
              curAudienceDataByTermId,
              curAudienceDataByGroupId,
            ].filter(Boolean)
            const isCorrectFunel =
              curAudienceData[0]?.additionalSlug.indexOf(
                cookieInfo.funnelId,
              ) !== -1
            const funnelParam = isCorrectFunel ? cookieInfo.funnelId : ''

            if (spotlight.listSessions.length === 0) {
              // IN CASE SLUGS IN COOKIE IS CORRECT
              if (curAudienceData.length > 0) {
                fetchSpotlight(curPage, curAudienceData[0].groupId, funnelParam)
                handleRedirectPage({
                  pageSlug: curPage,
                  audienceSlug: curAudienceData[0].groupId,
                  funnelSlug: funnelParam,
                })
                // IN CASE SLUGS IN COOKIE IS INCORRECT BUT HAS ONLY ONE AUDIENCE GROUP
              } else if (spotlight.listGroup.length === 1) {
                fetchSpotlight(
                  curPage,
                  spotlight.listGroup[0]?.groupId,
                  spotlight.additionalSlugActive,
                )
                handleRedirectPage({
                  pageSlug: curPage,
                  audienceSlug: spotlight.listGroup[0].groupId,
                  funnelSlug: spotlight.additionalSlugActive,
                })
              } else {
                // CHECK IF SLUGS IN COOKIE IS INCORRECT
                handleRedirectPage({ pageSlug: curPage })
                setPageType('selection')
              }
            } else {
              // in case wrong funnel slug
              funnelSlug &&
                spotlight.generalGroup?.additionalSlug &&
                funnelSlug !== spotlight.generalGroup.additionalSlug &&
                handleRedirectPage({
                  pageSlug: curPage,
                  audienceSlug: spotlight.groupIdActive,
                })
              setPageType('detail')
            }
            if (!cookieInfo?.audienceTermId && spotlight.generalGroup?.termId) {
              // HANDLE IN CASE PUBLISHED PAGE DON'T SAVE AUDIENCE TERM ID YET
              setAudienceTermId(curPage, spotlight.generalGroup?.termId || '')
            }
          } else {
            setPageType('selection')
          }
        }
      } else {
        if (
          spotlight.groupIdActive !== audienceSlug ||
          spotlight.additionalSlugActive !== funnelSlug
        ) {
          fetchSpotlight(curPage, audienceSlug, funnelSlug)
          if (
            !(locale === LOCALE.Taiwan && funnelSlug === 'yam-18-24') &&
            spotlight.groupIdActive === audienceSlug &&
            spotlight.additionalSlugActive !== funnelSlug
          ) {
            handleRedirectPage({
              pageSlug: curPage,
              audienceSlug: audienceSlug,
            })
          }
        }
      }
    }
  }, [asPath])

  useEffect(() => {
    if (!isDisableCookiePage) {
      const cookieInfo = getPageCookies(curPage)
      !cookieInfo &&
        audienceSlug && //EXCEPT SINGLE PAGE
        addPageCookies({
          pageId: spotlight?.postName,
          audienceId: spotlight?.generalGroup?.groupId || '',
          audienceTermId: spotlight?.generalGroup?.termId || '',
          funnelId: spotlight?.additionalSlugActive || '',
        })
    }
  }, [spotlight])

  useEffect(() => {
    if (!isDisableCookiePage && pageType === 'detail') {
      Cookies.set(
        SERVICE_CONNECTION_LATEST_ACCESS_COOKIES,
        JSON.stringify({
          pageId: spotlight.postName,
          audienceId: spotlight.groupIdActive,
          funnelId: spotlight.additionalSlugActive,
        }),
      )
    }
  }, [])

  const handleRenderPage =
    pageType === 'detail' ? (
      spotlight.listSessions.length > 0 ? (
        <CarePathDefaultTemplateHandler
          key={`${audienceSlug}-${
            spotlight?.generalGroup?.termId
          }-${funnelSlug}-${String(pageType)}`}
          spotlight={spotlight}
          hideSticky={hideSticky}
          footerInfo={footerInfo}
          dataNavMenuContent={dataNavMenuContent}
          isInline={isInline}
        />
      ) : (
        <NotFound />
      )
    ) : pageType === 'selection' ? (
      <CarePathSectionTemplate
        spotlight={spotlight}
        footerInfo={footerInfo}
        dataNavMenuContent={dataNavMenuContent}
        isInline={isInline}
      />
    ) : (
      <NavigatorLayout
        title={spotlight?.postTitle}
        description={spotlight?.postTitle}
        image={LOCALE_SPECS[locale].HOMEPAGE_IMAGE}
        isSponsored={spotlight?.isSponsor}
        sponsoredType="content"
        sponsoredBy={spotlight?.sponsor}
        // isHomePage={false}
        SEOData={{
          title: spotlight?.postTitle,
          description: spotlight?.postTitle,
          apiMetaData: spotlight?.metaTag,
          isIndexable: true,
        }}
        type="profile"
        footerInfo={footerInfo}
        languageOptions={spotlight?.translateOptions || []}
        dataNavMenuContent={dataNavMenuContent}
        isInline={isInline}
      >
        <Stack align={'center'} justify={'center'}>
          <Box
            sx={{
              padding: '20px 0',
            }}
          >
            <Loading />
          </Box>
        </Stack>
      </NavigatorLayout>
    )

  useEffect(() => {
    if (spotlight.groupIdActive || spotlight.additionalSlugActive) {
      const hashSlug = uuidv5(
        (spotlight.groupIdActive || '') +
          (spotlight.additionalSlugActive ? '/' : '') +
          (spotlight.additionalSlugActive || ''),
        uuidv5.URL,
      )
      const googletag = window.googletag || { cmd: [] }
      googletag.cmd.push(function () {
        googletag.pubads().setTargeting('CarePath', [hashSlug])
      })
    }
  }, [spotlight.groupIdActive, spotlight.additionalSlugActive])

  return (
    <>
      {locale === LOCALE.Indonesia &&
      asPath.startsWith('/festival-soya-2024/') ? (
        <Head>
          <script
            dangerouslySetInnerHTML={{
              __html: `!function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1118322649382200');
              fbq('track', 'PageView');`,
            }}
          ></script>
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src="https://www.facebook.com/tr?id=1118322649382200&ev=PageView&noscript=1"
              alt=""
            />
          </noscript>
        </Head>
      ) : null}
      {handleRenderPage}
    </>
  )
}

export default SpotlightCarePathTemplate
